/**
 * Pure layout maths for fitting video to its stream's aspect ratio. No DOM access, so the rules
 * are the same wherever they are used and easy to reason about.
 */

export interface Size {
  width: number
  height: number
}

export interface Rect extends Size {
  x: number
  y: number
}

export const sameSize = (a: Size | null, b: Size | null): boolean =>
  a === b || (!!a && !!b && a.width === b.width && a.height === b.height)

export const sameRect = (a: Rect | null, b: Rect | null): boolean =>
  a === b || (!!a && !!b && a.x === b.x && a.y === b.y && sameSize(a, b))

/**
 * The largest rectangle with the source's aspect ratio that fits inside `box`, centred. This is
 * CSS `object-fit: contain` computed up front, so the element can be sized to the picture itself
 * instead of letterboxing it inside a box of the wrong shape.
 */
export const fitContain = (source: Size, box: Size): Rect => {
  if (
    source.width <= 0 ||
    source.height <= 0 ||
    box.width <= 0 ||
    box.height <= 0
  ) {
    return {
      x: 0,
      y: 0,
      width: Math.max(0, box.width),
      height: Math.max(0, box.height)
    }
  }
  const scale = Math.min(box.width / source.width, box.height / source.height)
  const width = Math.round(source.width * scale)
  const height = Math.round(source.height * scale)
  return {
    x: Math.round((box.width - width) / 2),
    y: Math.round((box.height - height) / 2),
    width,
    height
  }
}

/** Aspect ratio assumed for a small view until its stream reports its size (webcam default). */
export const DEFAULT_PREVIEW_RATIO = 4 / 3

export interface PreviewPlacementOptions {
  /** Space kept to the stage edges and to the main video. */
  margin: number
  /** Space between two small views. */
  gap: number
  /** Preferred width of a small view as a fraction of the stage width... */
  widthFraction: number
  /** ...clamped to this range. Below `minWidth` a side gutter is not used. */
  minWidth: number
  maxWidth: number
  /** Smallest height for views placed in a row in the band above the main video. */
  minRowHeight: number
  /** Width cap when the views have to float over the main video (keeps them unobtrusive). */
  overlayMaxWidth: number
  /** Height kept free at the bottom of the stage (name badge, call controls). */
  bottomReserve: number
}

export const DEFAULT_PREVIEW_OPTIONS: PreviewPlacementOptions = {
  margin: 16,
  gap: 10,
  widthFraction: 0.22,
  minWidth: 96,
  maxWidth: 200,
  minRowHeight: 56,
  overlayMaxWidth: 160,
  bottomReserve: 72
}

export interface PreviewPlacement {
  rects: Rect[]
  /** True when no free space was large enough and the views float over the main video. */
  overlapsMain: boolean
}

/**
 * Where to put the small views (the local self-preview, and a screen share if any).
 *
 * Each view gets its own stream's aspect ratio. They go into the free space around the main
 * video so they never cover the other participant: first a side gutter (a portrait phone stream
 * in a landscape stage leaves wide ones), then the band above the video (a landscape stream on a
 * tall phone screen). Only when the main video fills the stage do they float over its top-left
 * corner, which is where the self-preview has always been.
 *
 * `main` is the rectangle the main video is drawn in, or null when it is not known (camera off,
 * not measured yet); the views then sit in the stage's top-left corner.
 */
export const placePreviews = (
  stage: Size,
  main: Rect | null,
  aspectRatios: number[],
  options: PreviewPlacementOptions = DEFAULT_PREVIEW_OPTIONS
): PreviewPlacement => {
  const count = aspectRatios.length
  if (!count || stage.width <= 0 || stage.height <= 0) {
    return { rects: [], overlapsMain: false }
  }
  const { margin, gap } = options
  const ratios = aspectRatios.map((r) =>
    r > 0 && isFinite(r) ? r : DEFAULT_PREVIEW_RATIO
  )
  const gaps = gap * (count - 1)

  // Widest column of views (one above the other) that still fits the stage height.
  const inverseSum = ratios.reduce((sum, r) => sum + 1 / r, 0)
  const columnHeightLimit = Math.max(
    0,
    stage.height - 2 * margin - options.bottomReserve
  )
  const widthForColumn = Math.max(0, (columnHeightLimit - gaps) / inverseSum)
  const preferredWidth = Math.min(
    options.maxWidth,
    Math.max(options.minWidth, stage.width * options.widthFraction)
  )
  const baseWidth = Math.max(1, Math.min(preferredWidth, widthForColumn))

  const column = (x: number, y: number, width: number): Rect[] => {
    let top = y
    return ratios.map((ratio) => {
      const height = Math.round(width / ratio)
      const rect = {
        x: Math.round(x),
        y: Math.round(top),
        width: Math.round(width),
        height
      }
      top += height + gap
      return rect
    })
  }

  if (main) {
    // 1. A side gutter beside the main video: left first, then right.
    const leftFree = main.x
    const rightFree = stage.width - (main.x + main.width)
    for (const side of ['left', 'right'] as const) {
      const free = side === 'left' ? leftFree : rightFree
      const width = Math.min(baseWidth, free - 2 * margin)
      if (width >= options.minWidth) {
        const x = side === 'left' ? margin : stage.width - margin - width
        return { rects: column(x, margin, width), overlapsMain: false }
      }
    }

    // 2. The band above the main video: one row of views of equal height.
    const rowHeightLimit = main.y - 2 * margin
    const ratioSum = ratios.reduce((sum, r) => sum + r, 0)
    const rowHeight = Math.min(
      rowHeightLimit,
      (stage.width - 2 * margin - gaps) / ratioSum,
      preferredWidth / Math.max(...ratios)
    )
    if (rowHeight >= options.minRowHeight) {
      let left = margin
      const rects = ratios.map((ratio) => {
        const width = Math.round(rowHeight * ratio)
        const rect = {
          x: Math.round(left),
          y: margin,
          width,
          height: Math.round(rowHeight)
        }
        left += width + gap
        return rect
      })
      return { rects, overlapsMain: false }
    }

    // 3. No free space: float over the main video's top-left corner, kept small.
    return {
      rects: column(
        Math.max(0, main.x) + margin,
        Math.max(0, main.y) + margin,
        Math.min(baseWidth, Math.max(options.overlayMaxWidth, options.minWidth))
      ),
      overlapsMain: true
    }
  }

  return { rects: column(margin, margin, baseWidth), overlapsMain: false }
}
